import { useState, useCallback, useRef } from 'react';
import { analyzeLlmsTxtAI } from '../firebase/ai';

/**
 * Hook for AI-powered llms.txt analysis
 * Handles loading states, errors, and analysis results
 */
export const useLlmsTxtAnalysis = () => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  const analyzedomains = useCallback(async (domains, organizationName, industry = '') => {
    // Validate input
    if (!domains || domains.length === 0) {
      setError('No domains provided');
      return null;
    }

    if (!organizationName?.trim()) {
      setError('Organization name is required');
      return null;
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const result = await analyzeLlmsTxtAI(domains, organizationName.trim(), industry);

      // Check if request was aborted
      if (abortControllerRef.current?.signal.aborted) {
        return null;
      }

      const data = result.data || result;
      setAnalysis(data);
      return data;
    } catch (err) {
      // Don't set error if request was aborted
      if (err.name === 'AbortError') {
        return null;
      }
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearAnalysis = useCallback(() => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setAnalysis(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    analysis,
    loading,
    error,
    analyzedomains,
    clearAnalysis
  };
};

export default useLlmsTxtAnalysis;
